import { useCallback, useEffect, useMemo, useRef } from "react";
import { createCarouselLongPressHandlers } from "../gestures/createCarouselLongPressHandlers";

/**
 * Attaches iOS-safe long-press detection to a single interactive element.
 * Uses non-passive touch/pointer listeners when preventDefaultOnStart is enabled.
 */
export function useLongPressAction({
  onLongPress,
  enabled = true,
  preventDefaultOnStart = false,
  isLongPressBlockedRef = null,
}) {
  const ref = useRef(null);
  const onLongPressRef = useRef(onLongPress);
  const handlersRef = useRef(null);
  const blockedRef = useRef(isLongPressBlockedRef);
  blockedRef.current = isLongPressBlockedRef;
  onLongPressRef.current = onLongPress;

  const handlers = useMemo(
    () => {
      const nextHandlers = createCarouselLongPressHandlers({
        onLongPress: () => onLongPressRef.current?.(),
        isExcludedTarget: () => blockedRef.current?.current?.() === true,
        preventDefaultOnStart,
      });
      handlersRef.current = nextHandlers;
      return nextHandlers;
    },
    [preventDefaultOnStart],
  );

  const cancelActiveGesture = useCallback(() => {
    handlersRef.current?.cancelActiveGesture?.();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || !onLongPress) {
      return undefined;
    }

    const {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onLostPointerCapture,
      onContextMenu,
      onClickCapture,
    } = handlers;

    const touchOptions = handlers.usesPreventDefaultOnStart
      ? { passive: false }
      : { passive: true };

    el.addEventListener("touchstart", onTouchStart, touchOptions);
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchCancel, { passive: true });

    el.addEventListener("pointerdown", onPointerDown, touchOptions);
    el.addEventListener("pointermove", onPointerMove, { passive: true });
    el.addEventListener("pointerup", onPointerUp, { passive: true });
    el.addEventListener("pointercancel", onPointerCancel, { passive: true });
    el.addEventListener("lostpointercapture", onLostPointerCapture, {
      passive: true,
    });

    el.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("click", onClickCapture, true);

    return () => {
      el.removeEventListener("touchstart", onTouchStart, touchOptions);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchCancel);

      el.removeEventListener("pointerdown", onPointerDown, touchOptions);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerCancel);
      el.removeEventListener("lostpointercapture", onLostPointerCapture);

      el.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, [handlers, enabled, onLongPress]);

  return { ref, cancelActiveGesture };
}
